import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { htmlSafe } from '@ember/template';
import { service } from '@ember/service';
import { modifier } from 'ember-modifier';
import { restartableTask } from 'ember-concurrency';
import type { Store as StoreService } from '@warp-drive/core';
import Modal from 'tio-ui/components/modal';
import Button from 'tio-ui/components/button';
import jsonPrettyPrint from '../../../../../helpers/json-pretty-print.ts';
import { recommendationPromptVersionQuery } from 'tio-data/requests/internal-ai';
import type { RecommendationPromptVersion } from 'tio-data/data/resources/recommendation-prompt-version.type';

/**
 * The prompt version that produced a recommendation, opened from the
 * `Prompt <set> v<n>` caption on the analysis card.
 *
 * Unlike the policy, the prompt text is not in the recommendation context —
 * only its identity is — so this fetches. Lazily, once the modal is actually
 * opened: an approver who never asks pays nothing, and reopening the modal on
 * the same identity does not refetch. Only the identity on screen is retained,
 * so moving to another recommendation and back does fetch again — one extra
 * GET on a staff-only path, which is not worth an eviction policy.
 */
export interface PromptDetailModalSignature {
  Args: {
    isOpen: boolean;
    onClose: () => void;
    /** The stored identity, e.g. `db:634/TA:v1`. */
    identity: string;
    /** The rendered caption, e.g. "Prompt 634/TA v1 (prompt store)". */
    versionLabel?: string;
    /** The recommendation context, offered as what was sent with the prompt. */
    context?: Record<string, unknown>;
    /** When the analysis ran, used to detect a reused version number. */
    analysisCreatedAt?: string;
  };
}

/**
 * Recorded for provenance but stripped before the context is serialized into
 * the prompt (`PROVENANCE_ONLY_CONTEXT_KEYS` in the agent), so including it
 * here would misstate what the model was given.
 */
const PROVENANCE_ONLY_KEYS = ['policy_version'];

/**
 * Left in the stored template and substituted per application at run time.
 * Named here rather than inline so the braces are data, not something the
 * template compiler has to be talked out of parsing.
 */
const RUNTIME_PLACEHOLDERS = '{{LIMITS_CLAUSE}} and {{DEADLINE_CLAUSE}}';

class PromptDetailModal extends Component<PromptDetailModalSignature> {
  @service declare store: StoreService;

  /**
   * Loaded state is stored WITH the identity it belongs to, and read back
   * through getters that require a match. This component is reused rather than
   * rebuilt when the drawer moves to another recommendation, so an unkeyed
   * cache would show the previous prompt under the new identity — and clearing
   * eagerly instead would mean writing tracked state during render.
   */
  @tracked loadedPromptVersion?: RecommendationPromptVersion;
  @tracked loadedIdentity?: string;
  @tracked errorStatus?: number;
  @tracked erroredIdentity?: string;
  @tracked showValidationPrompt = false;
  @tracked showContext = false;

  get promptVersion() {
    return this.loadedIdentity === this.args.identity ? this.loadedPromptVersion : undefined;
  }

  placeholders = RUNTIME_PLACEHOLDERS;

  /** The context minus the keys the agent never sent to the model. */
  get sentContext() {
    const context = this.args.context;
    if (!context) return undefined;

    return Object.fromEntries(
      Object.entries(context).filter(([key]) => !PROVENANCE_ONLY_KEYS.includes(key))
    );
  }

  /**
   * Only a published version's content is frozen. tio-api edits a draft in
   * place under the same version number, so a draft identity names a moving
   * target — the text below may not be the text that ran. Shown rather than
   * withheld (during a backtest triage the current text still beats nothing),
   * but never presented as history without saying so.
   */
  get isUnstableVersion() {
    return !!this.promptVersion && this.promptVersion.status !== 'published';
  }

  /**
   * A version row created AFTER the analysis cannot be the version that ran —
   * the agent can only resolve a version that already exists. That happens
   * when a deleted draft's number is handed out again (tio-api#10486), and it
   * is the case `isUnstableVersion` cannot catch, since the replacement may
   * since have been published and would otherwise look authoritative.
   */
  get isDifferentVersion() {
    const created = Date.parse(this.promptVersion?.createdAt ?? '');
    const analyzed = Date.parse(this.args.analysisCreatedAt ?? '');

    return Number.isFinite(created) && Number.isFinite(analyzed) && created > analyzed;
  }

  get errorMessage() {
    if (this.erroredIdentity !== this.args.identity) return undefined;
    // the endpoint is internal-staff-only, and this drawer is also mounted for
    // partner admins in tio-employee
    if (this.errorStatus === 403) return 'Prompt text is available to Tuition.io staff.';
    if (this.errorStatus === 404) {
      return 'This prompt version is no longer stored, so its text cannot be shown.';
    }
    return 'The prompt text could not be loaded.';
  }

  // restartable, not drop: when the identity changes mid-flight the new one
  // supersedes the old, and dropping it would leave the new prompt unloaded
  loadPrompt = restartableTask(async (identity: string) => {
    // a published version's content is immutable, so a load that succeeded for
    // THIS identity never needs repeating
    if (this.loadedPromptVersion && this.loadedIdentity === identity) return;

    try {
      const { content } = (await this.store.request(
        recommendationPromptVersionQuery(identity)
      )) as { content: { data: RecommendationPromptVersion } };
      this.loadedPromptVersion = content.data;
      this.loadedIdentity = identity;
      this.erroredIdentity = undefined;
    } catch (error) {
      this.errorStatus = (error as { status?: number })?.status;
      this.erroredIdentity = identity;
    }
  });

  /**
   * The modal renders its body only once opened, so insertion is the signal
   * that the prompt has actually been asked for. Identity is a tracked
   * argument of the modifier, so a change while the modal is open reloads
   * rather than leaving the previous prompt on screen.
   */
  loadOnOpen = modifier((_element: Element, [identity]: [string]) => {
    this.loadPrompt.perform(identity);
  });

  toggleValidationPrompt = () => {
    this.showValidationPrompt = !this.showValidationPrompt;
  };

  toggleContext = () => {
    this.showContext = !this.showContext;
  };

  <template>
    <Modal
      @isOpen={{@isOpen}}
      @onClose={{@onClose}}
      @size="4xl"
      @ariaLabel="Prompt detail"
      data-test-prompt-detail-modal
    >
      <:header>
        <h3 class="tio-text-heading tio-fg-primary">{{if @versionLabel @versionLabel "Prompt"}}</h3>
        <p class="tio-text-caption" data-test-prompt-identity>
          {{@identity}}{{#if this.promptVersion}}&nbsp;&middot; checksum
            {{this.promptVersion.checksum}}{{/if}}
        </p>
      </:header>
      <:body>
        <div {{this.loadOnOpen @identity}}>
          {{#if this.loadPrompt.isRunning}}
            <p class="text-sm tio-fg-muted" data-test-prompt-loading>Loading the prompt…</p>
          {{else if this.errorMessage}}
            <p class="text-sm tio-fg-muted" data-test-prompt-error>{{this.errorMessage}}</p>
          {{else if this.promptVersion}}
            {{#if this.isDifferentVersion}}
              <p class="tio-text-caption mb-3" data-test-prompt-superseded>
                This version was created after the analysis ran, so it is not the prompt that
                produced this recommendation — its version number was reused.
              </p>
            {{else if this.isUnstableVersion}}
              <p class="tio-text-caption mb-3" data-test-prompt-unstable>
                This is a
                {{this.promptVersion.status}}
                version. Only published versions are frozen once used, so this text may have been
                edited since the recommendation ran.
              </p>
            {{/if}}
            <p class="tio-text-caption mb-3">
              The stored template. Two clauses,
              <code class="tio-fg-secondary">{{this.placeholders}}</code>, are substituted per
              application when the recommendation runs.
            </p>
            <pre
              class="text-sm whitespace-pre-wrap tio-bg-sunken tio-fg-primary p-3 rounded"
              data-test-prompt-main
            >{{this.promptVersion.mainPrompt}}</pre>

            <div class="mt-4">
              <Button
                @appearance="minimal"
                @size="sm"
                @testHook="ai-analysis-prompt__validation-toggle"
                data-test-prompt-validation-toggle
                {{on "click" this.toggleValidationPrompt}}
              >
                {{if this.showValidationPrompt "Hide" "Show"}}
                the validation prompt
              </Button>
              {{#if this.showValidationPrompt}}
                <pre
                  class="text-sm whitespace-pre-wrap tio-bg-sunken tio-fg-primary p-3 rounded mt-2"
                  data-test-prompt-validation
                >{{this.promptVersion.validationPrompt}}</pre>
              {{/if}}
            </div>

            {{#if this.sentContext}}
              <div class="mt-4">
                <Button
                  @appearance="minimal"
                  @size="sm"
                  @testHook="ai-analysis-prompt__context-toggle"
                  data-test-prompt-context-toggle
                  {{on "click" this.toggleContext}}
                >
                  {{if this.showContext "Hide" "Show"}}
                  the application context sent with this prompt
                </Button>
                {{#if this.showContext}}
                  <div class="mt-2" data-test-prompt-context>
                    {{htmlSafe (jsonPrettyPrint this.sentContext)}}
                  </div>
                {{/if}}
              </div>
            {{/if}}
          {{/if}}
        </div>
      </:body>
    </Modal>
  </template>
}

export default PromptDetailModal;
